// ─────────────────────────────────────────────────────────────────────────────
// src/hooks/convertSedationDetailDto.ts
// Single source of truth for SedationDetailDto (API) → SedationDetail (local) conversion.
// Used by both usePatientSedations and useUpcomingPrefetch so the two flows can't drift.
// ─────────────────────────────────────────────────────────────────────────────
import type { SedationDetailDto } from '../api/generated';
import { FieldOfSurgery } from '../api/generated/models/FieldOfSurgery';
import type { SedationDetail } from './repos/sedationDetailRepo';

export function convertSedationDetailDtoToLocal(
  dto: SedationDetailDto,
  fallbackPatientId?: string
): SedationDetail {
  return {
    Id: dto.id || '',
    CreatedDateTime: dto.createdDateTime || new Date().toISOString(),
    Synced: true,
    Deactivated: false,

    PatientId: dto.patientId || fallbackPatientId || '',
    Reason: dto.reason || null,
    Procedure: dto.procedure || null,
    Surgeon: dto.surgeon || null,
    ConsentSigned: dto.consentSigned || false,
    Sedationist: dto.sedationist || null,
    Nurse: dto.nurse || null,
    FieldOfSurgery: dto.fieldOfSurgery || FieldOfSurgery.GENERAL_DENTISTRY,
    FieldOfSurgeryNote: dto.fieldOfSurgeryNote || null,
    PlannedSedationTechnique: (dto as any).plannedSedationTechnique || null,
    MultiDrugRationale: (dto as any).multiDrugRationale || null,
    Status: (dto.status as any) || 'Pending',
    Comments: dto.comments || null,
    Considerations: dto.considerations || null,
    BloodPressure: dto.bloodPressure
      ? { Systolic: dto.bloodPressure.systolic ?? null, Diastolic: dto.bloodPressure.diastolic ?? null }
      : { Systolic: null, Diastolic: null },
    PulseRate: dto.pulseRate ?? null,
    CO2EndTidal: dto.cO2EndTidal ?? null,
    ECG: dto.ecg || false,
    SAO2: dto.saO2 ?? null,
    RespiratoryRate: dto.respiratoryRate ?? null,
    ReplayCount: dto.replayCount || 0,
    BIS: dto.bis ?? null,
    CapnographyAttached: dto.capnographyAttached || false,
    BISAttached: dto.bisAttached || false,
    BlanketOverPatient: dto.blanketOverPatient || false,
    PatientPackUrl: dto.patientPackUrl || null,
    Cannulation: dto.cannulation
      ? {
          Size: dto.cannulation.size || null,
          Type: dto.cannulation.type || null,
          Site: dto.cannulation.site || null,
          TimeHrs: dto.cannulation.timeHrs ? parseFloat(dto.cannulation.timeHrs) : null,
          TimeMins: dto.cannulation.timeMins ? parseFloat(dto.cannulation.timeMins) : null,
        }
      : { Size: null, Type: null, Site: null, TimeHrs: null, TimeMins: null },
    PdfUrl: dto.pdfUrl || null,
    FileType: (dto.fileType as any) || null,
    SedationistId: dto.sedationistId || null,
    SedationistEmail: dto.sedationistEmail || null,
    AppointmentId: dto.appointmentId || null,
    AppointmentStartDateTime: dto.appointmentStartDateTime || null,
    AppointmentEndDateTime: dto.appointmentEndDateTime || null,
    ClinicName: dto.clinicName || '',
    ClinicPhoneNumber: dto.clinicPhoneNumber || '',

    // KeyPoints Review (pre-sedation checklist / consent confirmation)
    KeyPoints: dto.keyPointsReview
      ? {
          PreSedation: {
            InformationPackReceived: dto.keyPointsReview.preSedation?.informationPackReceived ?? true,
            PreOpExplanationGiven: dto.keyPointsReview.preSedation?.preOpExplanationGiven ?? true,
            PatientUnderstandsProcedure: dto.keyPointsReview.preSedation?.patientUnderstandsProcedure ?? true,
          },
          Technique: {
            IntendedLevel: (dto.keyPointsReview.technique?.intendedLevel as any) || 'Moderate',
            IntendedLevels: ((dto.keyPointsReview.technique as any)?.intendedLevels as any) || undefined,
            NotAsleepOrUnconscious: dto.keyPointsReview.technique?.notAsleepOrUnconscious ?? true,
            AimAnxiolysisWithResponsiveness: dto.keyPointsReview.technique?.aimAnxiolysisWithResponsiveness ?? true,
            PossibleAmnesiaExplained: dto.keyPointsReview.technique?.possibleAmnesiaExplained ?? true,
            LocalAnaestheticBySurgeon: dto.keyPointsReview.technique?.localAnaestheticBySurgeon ?? true,
            RisksExplainedPONVAndComplications: dto.keyPointsReview.technique?.risksExplainedPONVAndComplications ?? true,
            EscortArrangementsConfirmed: dto.keyPointsReview.technique?.escortArrangementsConfirmed ?? true,
            PostSedationInstructionsGiven: dto.keyPointsReview.technique?.postSedationInstructionsGiven ?? true,
          },
          Consent: {
            WrittenConsentReconfirmedVerbally: dto.keyPointsReview.consent?.writtenConsentReconfirmedVerbally ?? true,
            PatientHappyToProceed: dto.keyPointsReview.consent?.patientHappyToProceed ?? true,
          },
          HostCompliance: (dto.keyPointsReview as any).hostCompliance
            ? {
                OxygenAndAirwayEquipmentChecked: (dto.keyPointsReview as any).hostCompliance.oxygenAndAirwayEquipmentChecked ?? true,
                ResuscitationEquipmentChecked: (dto.keyPointsReview as any).hostCompliance.resuscitationEquipmentChecked ?? true,
                EmergencyDrugsInDate: (dto.keyPointsReview as any).hostCompliance.emergencyDrugsInDate ?? true,
                EscalationConfirmed: (dto.keyPointsReview as any).hostCompliance.escalationConfirmed ?? true,
                SurgeonAndNurseIlsCertified: (dto.keyPointsReview as any).hostCompliance.surgeonAndNurseIlsCertified ?? true,
              }
            : null,
          Notes: dto.keyPointsReview.notes || null,
          RecordedAt: dto.keyPointsReview.recordedAt || null,
          RecordedBy: dto.keyPointsReview.recordedBy || null,
        }
      : undefined,

    // Suitability Review Information (IOSN / MDAS score)
    SuitabilityReviewInformation: dto.suitabilityReviewInformation
      ? {
          IOSNScore: dto.suitabilityReviewInformation.iosnScore ?? null,
          DiscussInConfidenceWithSedationist: dto.suitabilityReviewInformation.discussInConfidenceWithSedationist ?? null,
          FeelFearAnxietyDistressRequestedSedation:
            dto.suitabilityReviewInformation.feelFearAnxietyDistressRequestedSedation ?? null,
          DoctorRecommendedSedation: dto.suitabilityReviewInformation.doctorRecommendedSedation ?? null,
        }
      : undefined,

    // Health History Snapshot (Medical Legal Lock)
    HealthHistorySnapshot: dto.healthHistorySnapshot
      ? {
          MedicalHistory: dto.healthHistorySnapshot.medicalHistory ?? null,
          Medications: dto.healthHistorySnapshot.medications ?? null,
          Allergies: dto.healthHistorySnapshot.allergies ?? null,
          AnaestheticHistory: dto.healthHistorySnapshot.anaestheticHistory ?? null,
          ASAClassification: dto.healthHistorySnapshot.asaClassification ?? null,
          Height: dto.healthHistorySnapshot.height ?? null,
          Weight: dto.healthHistorySnapshot.weight ?? null,
          BMI: dto.healthHistorySnapshot.bmi ?? null,
          Smoking: dto.healthHistorySnapshot.smoking ?? null,
          Alcohol: dto.healthHistorySnapshot.alcohol ?? null,
          SnapshotDate: dto.healthHistorySnapshot.snapshotDate || new Date().toISOString(),
        }
      : undefined,
  };
}
